import React from 'react';

/* ── shared chrome: the in-browser app frame used across marketing mocks ── */
const Frame = ({ title, children }) => (
  <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-xl shadow-2xl overflow-hidden">
    <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-700">
      <span className="w-3 h-3 rounded-full bg-red-500" />
      <span className="w-3 h-3 rounded-full bg-yellow-500" />
      <span className="w-3 h-3 rounded-full bg-green-500" />
      <span className="mx-auto text-xs text-gray-400">{title}</span>
    </div>
    <div className="bg-stone-50 text-left">{children}</div>
  </div>
);

/* 1 — the identity card: what your AI knows about YOU */
const IdentityCardMock = () => (
  <Frame title="ManuscriptSmith — Author Knowledge">
    <div className="p-5">
      <div className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-amber-50 p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-amber-400 flex items-center justify-center text-white text-lg">✒️</div>
          <div>
            <div className="font-bold text-stone-900 text-sm">Maria's Author AI</div>
            <div className="text-[11px] text-stone-600"><b>4 knowledge sources</b> · <b>1987–2026</b> of your life · <b>2 connected libraries</b></div>
          </div>
        </div>
        <div className="mt-2 text-[11px] text-stone-700">voice: <b>warm, unhurried · long flowing sentences · imagery-rich</b></div>
        <div className="mt-1 text-[11px] text-emerald-700">🛡 Privacy Vault active — 12 items protected</div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
        {[['📄','Life timeline & bio'],['📚','2 finished books'],['📷','Photo timeline'],['📧','14 forwarded emails']].map(([i,l]) => (
          <div key={l} className="rounded-lg border border-stone-200 bg-white px-2.5 py-2 flex items-center gap-2"><span>{i}</span><span className="text-stone-700 font-medium">{l}</span></div>
        ))}
      </div>
    </div>
  </Frame>
);

/* 2 — voice: generic vs yours */
const VoiceMock = () => (
  <Frame title="ManuscriptSmith — Voice DNA">
    <div className="p-5 space-y-3">
      <div className="bg-white rounded-lg border border-stone-200 p-3">
        <div className="text-[10px] font-bold uppercase text-stone-400 mb-1">Generic AI</div>
        <p className="text-[12px] text-stone-500 italic">"My grandmother's house was old and full of memories."</p>
      </div>
      <div className="bg-white rounded-lg border-2 border-emerald-200 p-3">
        <div className="text-[10px] font-bold uppercase text-emerald-600 mb-1">In your voice — grounded in your facts</div>
        <p className="text-[12px] text-stone-800 italic">"Babusya's house on Kobylianska Street smelled of dill and beeswax, the way it had every summer since 1994."</p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {['Kobylianska Street — your timeline', '1994 — photo album', 'dill & beeswax — your Book 1'].map((t) => (
            <span key={t} className="rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[10px] text-emerald-800">{t}</span>
          ))}
        </div>
      </div>
    </div>
  </Frame>
);

/* 3 — privacy vault */
const VaultMock = () => (
  <Frame title="ManuscriptSmith — Privacy Vault">
    <div className="p-5 space-y-2">
      {[
        ['phone', '(80•) •••-••45', 'Life timeline', false],
        ['address', '14• Ko••••••••a St', 'Chapter 2 draft', false],
        ['email', 'ma•••@•••.com', 'Forwarded email', true],
      ].map(([type, preview, src, allowed]) => (
        <div key={preview} className="flex items-center justify-between rounded-lg border border-stone-200 bg-white px-3 py-2">
          <div>
            <span className="rounded-full bg-stone-100 px-1.5 py-0.5 text-[10px] uppercase text-stone-500 mr-2">{type}</span>
            <span className="text-[12px] font-mono text-stone-700">{preview}</span>
            <span className="ml-2 text-[10px] text-stone-400">{src}</span>
          </div>
          <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${allowed ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-amber-300 bg-amber-50 text-amber-900'}`}>
            {allowed ? 'authorized' : '[REDACTED] from AI'}
          </span>
        </div>
      ))}
      <p className="text-[10px] text-stone-400 pt-1">You see every item. No model does — until you authorize that exact item. Reversible, forever.</p>
    </div>
  </Frame>
);

/* 4 — audit: test what your AI knows */
const AuditMock = () => (
  <Frame title="ManuscriptSmith — Test what your AI knows">
    <div className="p-5">
      <div className="flex gap-2">
        <div className="flex-1 rounded-lg border border-stone-300 bg-white px-3 py-2 text-[12px] text-stone-700">the summer we moved to Lviv</div>
        <div className="rounded-lg bg-stone-900 px-3 py-2 text-[12px] font-semibold text-white">Show me</div>
      </div>
      <div className="mt-3 rounded-lg bg-white border border-stone-200 p-3">
        <div className="text-[10px] font-bold uppercase text-stone-400 mb-1.5">📖 Knowledge the AI would receive</div>
        <p className="text-[11px] text-stone-700 leading-relaxed">
          [Life timeline] <b>1998 — moved to Lviv</b>, two rooms above the bakery on Rynok Square…<br />
          [Photo timeline] <b>## 1998</b> — <b>LvivMove</b> — 32 photos (candid ×14, travel ×9)…
        </p>
      </div>
      <p className="mt-2 text-[10px] text-stone-400">Gaps show up as gaps — so you know exactly what to add next.</p>
    </div>
  </Frame>
);

/* 5 — fact mirror + drift guard */
const FactMirrorMock = () => (
  <Frame title="ManuscriptSmith — The Fact Mirror">
    <div className="p-5 space-y-2">
      <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-white px-3 py-2">
        <span className="text-[12px] text-stone-800">Moved to Lviv in 1998.</span>
        <span className="text-[11px] text-emerald-700 font-semibold">✓ confirmed</span>
      </div>
      <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2">
        <p className="text-[11px] text-amber-900">⚠️ <b>New upload</b> contradicts a fact you confirmed:</p>
        <p className="text-[11px] text-stone-700">You confirmed: "Moved to Lviv in 1998" · That source says: <b>"we arrived in 1999"</b></p>
        <div className="mt-1 flex gap-2">
          <span className="rounded bg-amber-700 px-2 py-0.5 text-[10px] font-semibold text-white">Re-review the fact</span>
          <span className="text-[10px] text-amber-800 underline">My fact stands — dismiss</span>
        </div>
      </div>
      <p className="text-[10px] text-stone-400 pt-1">Your AI never silently blends contradictions — it asks you which truth wins.</p>
    </div>
  </Frame>
);

/* 6 — in the editor */
const EditorMock = () => (
  <Frame title="ManuscriptSmith — Editor · The Bakery Years">
    <div className="p-5 bg-white">
      <p className="text-[12px] text-stone-800 leading-6" style={{ fontFamily: 'Georgia, serif' }}>
        The morning we left, the whole street came out to watch the truck.
        <span className="bg-emerald-100 px-0.5"> Babusya pressed a jar of honey into my hands like a passport.</span>
      </p>
      <div className="mt-3 rounded-lg border-2 border-emerald-200 bg-emerald-50/60 p-2.5">
        <div className="text-[10px] font-bold uppercase text-emerald-700 mb-1">✨ Muse — grounded in your knowledge</div>
        <p className="text-[11px] text-stone-700 italic">"…the courtyard on Rynok Square — the one from your 1998 LvivMove album — could open the next scene."</p>
      </div>
    </div>
  </Frame>
);

const FEATURES = [
  { mock: IdentityCardMock, kicker: 'Your Author AI, visible', title: 'It knows your life — and shows you exactly what it knows',
    body: 'Feed it your bio, timelines, documents, whole archives, Drive share links, forwarded emails — even your photo library becomes a chronological life timeline (dates and album names only; no image is ever stored). The identity card keeps the whole picture in view.' },
  { mock: VoiceMock, kicker: 'Voice DNA + your facts', title: 'It writes in your voice, from your real details',
    body: 'Voice DNA learns register, rhythm and vocabulary from your own prose. Combined with your knowledge, suggestions stop being internet averages — they carry your streets, your years, your people.' },
  { mock: VaultMock, kicker: 'The Privacy Vault', title: 'It guards your secrets — provably',
    body: 'Sensitive items are detected the moment you add a source. You see each one; every AI sees [REDACTED] — until you personally authorize that exact item. Reversible, forever.' },
  { mock: AuditMock, kicker: 'Full auditability', title: 'You can test exactly what your AI knows',
    body: 'Ask anything and see the precise knowledge your AI receives for that question. Find gaps, add sources, lock what it should never use. Your AI, under your control.' },
  { mock: FactMirrorMock, kicker: 'Fact Mirror + Drift Guard', title: 'You verify it — and it flags contradictions',
    body: 'Distill your sources into atomic facts, confirm or correct each one. When a new source contradicts a fact you confirmed, your AI raises its hand instead of silently blending.' },
  { mock: EditorMock, kicker: 'In the editor', title: 'It helps while you write',
    body: 'The same knowledge grounds the planning board, the writing muse and every rewrite — your AI is in the editor with you, honoring your facts.' },
];

/* connectors strip with mini-visuals */
const CONNECTORS = [
  { icon: '📄', title: 'Files & archives', visual: <div className="rounded-lg border-2 border-dashed border-stone-300 px-3 py-2.5 text-center text-[10px] text-stone-400">Drop .docx · .pdf · .epub · .zip · .eml</div>,
    body: 'Paste text, fetch a web page, or upload documents and whole archives — even email exports.' },
  { icon: '🔄', title: 'Drive sync', visual: <div className="flex gap-1.5"><div className="flex-1 rounded-lg border border-stone-300 bg-white px-2 py-1.5 text-[10px] text-stone-400 truncate">drive share link…</div><div className="rounded-lg bg-stone-900 px-2 py-1.5 text-[10px] font-semibold text-white">Sync</div></div>,
    body: 'Paste a share link, re-sync on demand. Docs, Sheets and Slides read as text — never your whole Drive.' },
  { icon: '📧', title: 'Forward-to-ingest', visual: <div className="rounded-lg border border-stone-300 bg-white px-2 py-1.5 font-mono text-[10px] text-stone-600 truncate">kb+a8x2k1@manuscriptsmith.com</div>,
    body: 'Your own private address. Forward anything — it becomes knowledge in minutes, credentials auto-stripped.' },
  { icon: '📷', title: 'Photo timeline', visual: <div className="rounded-lg border border-stone-200 bg-white px-2 py-1.5 text-[10px] text-stone-600"><b>## 2003</b> — PRIDE — 20 photos<br /><b>## 2008</b> — Engagement — 12 photos</div>,
    body: 'A photo ZIP or Takeout becomes the photographed chapters of your life — dates and albums only.' },
];

export default function AuthorAiPage() {
  return (
    <div className="min-h-screen bg-white">
      <section className="bg-gradient-to-br from-emerald-900 via-emerald-800 to-stone-900 text-white py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <p className="uppercase tracking-widest text-emerald-300 text-sm mb-3">ManuscriptSmith exclusive</p>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Your Author AI</h1>
          <p className="text-xl text-emerald-100 max-w-2xl mx-auto">
            Other tools give you an AI that writes. ManuscriptSmith gives you an AI that knows
            <em> you</em> — your life, your voice, your books — and provably protects what you share.
          </p>
        </div>
      </section>

      {FEATURES.map((f, i) => {
        const Mock = f.mock;
        return (
          <section key={f.title} className={i % 2 ? 'bg-stone-50 py-14 px-4' : 'py-14 px-4'}>
            <div className={`max-w-5xl mx-auto grid lg:grid-cols-2 gap-10 items-center`}>
              <div className={i % 2 ? 'lg:order-2' : ''}>
                <p className="uppercase tracking-wide text-emerald-700 text-xs font-bold mb-2">{f.kicker}</p>
                <h2 className="text-2xl font-bold text-stone-900 mb-3">{f.title}</h2>
                <p className="text-stone-600 leading-relaxed">{f.body}</p>
              </div>
              <div className={i % 2 ? 'lg:order-1' : ''}><Mock /></div>
            </div>
          </section>
        );
      })}

      <section className="bg-stone-50 py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-stone-900 text-center mb-3">Every way your life already exists, connected</h2>
          <p className="text-stone-600 text-center max-w-2xl mx-auto mb-8">
            Your knowledge is scattered across drives, inboxes and photo libraries. Each connector is consent-first:
            only what you point at is ever read, and the Privacy Vault applies to all of it.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {CONNECTORS.map((c) => (
              <div key={c.title} className="rounded-2xl border border-stone-200 bg-white p-5">
                <div className="text-2xl mb-2">{c.icon}</div>
                <h3 className="font-bold text-stone-900 text-sm mb-2">{c.title}</h3>
                <div className="mb-2">{c.visual}</div>
                <p className="text-xs text-stone-600 leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-stone-900 mb-3">Privacy is the product</h2>
          <p className="text-stone-600 mb-2">
            Everything your Author AI holds is yours alone: per-author isolation, originals never altered,
            sensitive items masked from every model unless you authorize each one — and you can watch it work.
          </p>
          <p className="text-stone-500 text-sm">No sharing between authors. No training on your material. Ever.</p>
        </div>
      </section>

      <section className="pb-20 px-4 text-center">
        <h2 className="text-2xl font-bold text-stone-900 mb-4">Meet your Author AI today</h2>
        <a href="https://app.manuscriptsmith.com/signup" className="inline-block rounded-xl bg-emerald-700 text-white font-semibold px-8 py-3 hover:bg-emerald-800">Get started</a>
      </section>
    </div>
  );
}
